import { useState, useEffect, useLayoutEffect, useRef, useCallback, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { FeatherIcon } from './FeatherIcon';

const TOOLTIP_MARGIN = 10;
const TOOLTIP_MAX_W = 352;
const TOOLTIP_MAX_H = 320;
const TOOLTIP_GAP = 8;

/** Hover to read help. Portal + viewport coords avoid transform/overflow clipping; delayed close so pointer can reach the panel.
 *  `trigger` replaces the info icon (e.g. a platform logo); `triggerClassName` replaces the button's padding/size. */
export function InfoHoverHint({ label, children, trigger, triggerClassName }: {
  label: string; children: ReactNode; trigger?: ReactNode; triggerClassName?: string;
}) {
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState<{ top: number; left: number; maxW: number; maxH: number } | null>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const tooltipRef = useRef<HTMLDivElement>(null);
  const closeTimerRef = useRef<number | null>(null);

  const clearCloseTimer = useCallback(() => {
    if (closeTimerRef.current != null) {
      window.clearTimeout(closeTimerRef.current);
      closeTimerRef.current = null;
    }
  }, []);

  const scheduleClose = useCallback(() => {
    clearCloseTimer();
    closeTimerRef.current = window.setTimeout(() => {
      setOpen(false);
      setPos(null);
      closeTimerRef.current = null;
    }, 200);
  }, [clearCloseTimer]);

  const handlePointerEnter = useCallback(() => {
    clearCloseTimer();
    setOpen(true);
  }, [clearCloseTimer]);

  const updatePosition = useCallback(() => {
    const trig = triggerRef.current;
    if (!trig) return;
    const br = trig.getBoundingClientRect();
    if (br.width < 1 && br.height < 1) return;

    const vw = window.innerWidth;
    const vh = window.innerHeight;
    const maxW = Math.min(TOOLTIP_MAX_W, vw - 2 * TOOLTIP_MARGIN);
    const maxH = Math.min(TOOLTIP_MAX_H, vh - 2 * TOOLTIP_MARGIN);

    const tip = tooltipRef.current;
    const tipRect = tip?.getBoundingClientRect();
    const tipH =
      tipRect && tipRect.height >= 1 ? Math.min(tipRect.height, maxH) : Math.min(200, maxH);
    const tipW =
      tipRect && tipRect.width >= 1 ? Math.min(tipRect.width, maxW) : maxW;

    let left = br.left;
    if (left + tipW > vw - TOOLTIP_MARGIN) {
      left = vw - TOOLTIP_MARGIN - tipW;
    }
    left = Math.max(TOOLTIP_MARGIN, left);

    let top = br.bottom + TOOLTIP_GAP;
    if (top + tipH > vh - TOOLTIP_MARGIN) {
      const aboveTop = br.top - tipH - TOOLTIP_GAP;
      if (aboveTop >= TOOLTIP_MARGIN) {
        top = aboveTop;
      } else {
        top = Math.max(TOOLTIP_MARGIN, Math.min(br.bottom + TOOLTIP_GAP, vh - TOOLTIP_MARGIN - tipH));
      }
    }

    setPos({ top, left, maxW, maxH });
  }, []);

  useLayoutEffect(() => {
    if (!open) {
      setPos(null);
      return;
    }
    updatePosition();
    const id = window.requestAnimationFrame(() => updatePosition());
    return () => window.cancelAnimationFrame(id);
  }, [open, updatePosition]);

  useEffect(() => {
    if (!open) return;
    const tip = tooltipRef.current;
    if (!tip) return;
    const ro = new ResizeObserver(() => updatePosition());
    ro.observe(tip);
    return () => ro.disconnect();
  }, [open, updatePosition]);

  useEffect(() => {
    if (!open) return;
    const onScrollOrResize = () => updatePosition();
    window.addEventListener('resize', onScrollOrResize);
    window.addEventListener('scroll', onScrollOrResize, true);
    document.addEventListener('scroll', onScrollOrResize, true);
    return () => {
      window.removeEventListener('resize', onScrollOrResize);
      window.removeEventListener('scroll', onScrollOrResize, true);
      document.removeEventListener('scroll', onScrollOrResize, true);
    };
  }, [open, updatePosition]);

  useEffect(
    () => () => {
      clearCloseTimer();
    },
    [clearCloseTimer],
  );

  const btnPad = triggerClassName ?? 'p-1 min-h-8 min-w-8';
  const iconSize = 15;

  return (
    <span
      className="relative inline-flex items-center align-middle"
      onMouseEnter={handlePointerEnter}
      onMouseLeave={scheduleClose}
    >
      <button
        ref={triggerRef}
        type="button"
        className={`inline-flex shrink-0 items-center justify-center rounded text-[var(--color-text-tertiary)] hover:text-[var(--color-accent-primary)] focus:outline-none focus-visible:ring-1 focus-visible:ring-[var(--color-accent-primary)] ${btnPad}`}
        aria-label={label}
        onFocus={handlePointerEnter}
        onBlur={scheduleClose}
      >
        {trigger ?? <FeatherIcon name="info" size={iconSize} />}
      </button>
      {open &&
        createPortal(
          <div
            ref={tooltipRef}
            role="tooltip"
            style={{
              position: 'fixed',
              zIndex: 10050,
              boxSizing: 'border-box',
              ...(pos
                ? {
                    top: pos.top,
                    left: pos.left,
                    maxWidth: pos.maxW,
                    maxHeight: pos.maxH,
                    visibility: 'visible' as const,
                  }
                : {
                    top: 0,
                    left: 0,
                    maxWidth: TOOLTIP_MAX_W,
                    maxHeight: TOOLTIP_MAX_H,
                    visibility: 'hidden' as const,
                    pointerEvents: 'none' as const,
                  }),
            }}
            className="overflow-y-auto rounded-lg border border-[var(--color-border-default)] bg-[var(--color-bg-card)] p-3 text-left text-xs text-[var(--color-text-secondary)] leading-relaxed shadow-xl"
            onMouseEnter={handlePointerEnter}
            onMouseLeave={scheduleClose}
          >
            {children}
          </div>,
          document.body,
        )}
    </span>
  );
}
